// Design frames: hifi/05-property-add.html (H5.1), wireframes/15-properties-m3.html.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ActionBar,
  Button,
  LinearProgress,
  Notice,
  PhotoGrid,
  Segmented,
  StageTracker,
  useSnackbar,
} from '@sauda/ui/kit';
import { MAX_PHOTOS } from '@sauda/shared';
import { Screen } from '../../app/chrome.js';
import { ApiError, api } from '../../lib/api.js';
import { uploadPhotos, usablePhotos } from '../../lib/photo-upload.js';
import {
  EMPTY_DRAFT,
  basicsProblems,
  draftToBody,
  type BasicsProblems,
  type PropertyDraft,
} from '../../lib/property-form.js';
import type { PropertyView } from '../../lib/types.js';
import { BasicsFields, DetailsFields } from './FactsFields.js';

const DRAFT_KEY = 'property-draft';

function loadDraft(): PropertyDraft {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw === null ? EMPTY_DRAFT : { ...EMPTY_DRAFT, ...(JSON.parse(raw) as PropertyDraft) };
  } catch {
    return EMPTY_DRAFT;
  }
}
function saveDraft(draft: PropertyDraft | null): void {
  try {
    if (draft === null) localStorage.removeItem(DRAFT_KEY);
    else localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // The draft is a convenience; without storage the form still works.
  }
}

interface Local {
  id: string;
  file: File;
  url: string;
}

/**
 * Add a property in three steps: photos and the kind, the basics, the details. Photos are kept
 * on the phone until the property is saved, then sent; the text is kept as a draft on request.
 */
export function PropertyNewScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const input = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<PropertyDraft>(loadDraft);
  const [photos, setPhotos] = useState<Local[]>([]);
  const [skipped, setSkipped] = useState(false);
  const [problems, setProblems] = useState<BasicsProblems>({});
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);
  const patch = (next: Partial<PropertyDraft>) => setDraft((d) => ({ ...d, ...next }));

  // The previews are object URLs: let them go when the screen does.
  const latest = useRef<Local[]>([]);
  latest.current = photos;
  useEffect(
    () => () => {
      for (const p of latest.current) URL.revokeObjectURL(p.url);
    },
    [],
  );

  const stages = useMemo(
    () => [
      { key: 'photos', label: t('property.stepPhotos') },
      { key: 'basics', label: t('property.stepBasics') },
      { key: 'details', label: t('property.stepDetails') },
    ],
    [t],
  );

  const addFiles = (files: File[]) => {
    const { usable, skipped: left } = usablePhotos(files);
    setSkipped(left > 0);
    const room = MAX_PHOTOS - photos.length;
    setPhotos((current) => [
      ...current,
      ...usable.slice(0, room).map((file) => ({
        id: `${file.name}-${String(file.size)}-${String(Math.random())}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
    if (input.current) input.current.value = '';
  };
  const move = (id: string, direction: -1 | 1) =>
    setPhotos((current) => {
      const index = current.findIndex((p) => p.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target] as Local, next[index] as Local];
      return next;
    });

  const create = useMutation({
    mutationFn: async () => {
      const created = await api<PropertyView>('/properties', {
        method: 'POST',
        body: draftToBody(draft),
      });
      let uploadFailed = false;
      if (photos.length > 0) {
        try {
          await uploadPhotos(
            created.id,
            photos.map((p) => p.file),
            (done) => setSent(done),
          );
        } catch {
          uploadFailed = true;
        }
      }
      return { created, uploadFailed };
    },
    onSuccess: async ({ created, uploadFailed }) => {
      saveDraft(null);
      await cache.invalidateQueries({ queryKey: ['properties'] });
      void navigate(`/properties/${created.id}`, {
        replace: true,
        state: { saved: true, uploadFailed },
      });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status === 403
          ? t('property.notAllowed')
          : t('property.generic'),
      ),
  });

  const next = () => {
    if (step === 1) {
      const found = basicsProblems(draft);
      setProblems(found);
      if (Object.keys(found).length > 0) return;
    }
    setStep(step + 1);
  };

  return (
    <Screen
      title={t('property.addShort')}
      backIsClose={step === 0}
      eyebrow={t('property.stepOf', { step: step + 1, total: stages.length })}
      back={() => (step === 0 ? void navigate('/properties') : setStep(step - 1))}
    >
      <StageTracker stages={stages} current={step} label={t('property.steps')} />
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      {step === 0 ? (
        <div className="form-stack">
          <Segmented
            block
            label={t('property.kind')}
            value={draft.kind}
            onChange={(v) => patch({ kind: v as 'rent' | 'sale' })}
            options={[
              { value: 'rent', label: t('property.kindRent') },
              { value: 'sale', label: t('property.kindSale') },
            ]}
          />
          <PhotoGrid
            label={t('photos.title')}
            photos={photos.map((p, i) => ({
              id: p.id,
              url: p.url,
              status: 'ready',
              alt: t('photos.alt', { n: i + 1 }),
            }))}
            onMove={move}
            onRemove={(id) => setPhotos((c) => c.filter((p) => p.id !== id))}
            add={{
              label: t('photos.addShort'),
              disabled: photos.length >= MAX_PHOTOS,
              onClick: () => input.current?.click(),
            }}
            labels={{
              earlier: t('photos.earlier'),
              later: t('photos.later'),
              remove: t('photos.remove'),
              cover: t('photos.cover'),
              processing: t('photos.status_pending'),
              rejected: t('photos.status_rejected'),
            }}
          />
          <input
            ref={input}
            type="file"
            className="k-sr-only"
            accept="image/jpeg,image/png,image/webp"
            multiple
            aria-label={t('photos.choose')}
            onChange={(e) => addFiles(Array.from(e.target.files ?? []))}
          />
          <p className="k-t-body-sm k-muted">{t('photos.hint', { max: MAX_PHOTOS })}</p>
          {skipped ? <Notice tone="info">{t('photos.skipped')}</Notice> : null}
        </div>
      ) : null}
      {step === 1 ? <BasicsFields draft={draft} onChange={patch} problems={problems} /> : null}
      {step === 2 ? <DetailsFields draft={draft} onChange={patch} /> : null}
      {create.isPending && sent !== null ? (
        <LinearProgress
          label={t('photos.uploading')}
          value={(sent / Math.max(photos.length, 1)) * 100}
        />
      ) : null}
      <ActionBar label={t('property.steps')}>
        {step === 0 ? (
          <Button
            variant="text"
            size="lg"
            onClick={() => {
              saveDraft(draft);
              snack({ text: t('property.draftKept') });
            }}
          >
            {t('property.saveDraft')}
          </Button>
        ) : (
          <Button variant="text" size="lg" onClick={() => setStep(step - 1)}>
            {t('app.back')}
          </Button>
        )}
        {step < 2 ? (
          <Button size="lg" onClick={next}>
            {t('property.next')}
          </Button>
        ) : (
          <Button size="lg" loading={create.isPending} onClick={() => create.mutate()}>
            {t('property.create')}
          </Button>
        )}
      </ActionBar>
    </Screen>
  );
}
